import React, { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription } from "./ui/alert";
import { Switch } from "./ui/switch";
import { CheckCircle, Zap, Upload, ArrowRight, ArrowLeft, AlertCircle } from "lucide-react";
import { getAuthHeaders } from "../lib/authHeaders";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const COMMON_CONCERNS = ["Weight management", "Low energy/fatigue", "Brain fog", "Joint pain", "Poor sleep", "Low testosterone", "Gut health", "Inflammation", "Cognitive decline", "Muscle recovery", "Anti-aging", "Immune support"];
const COMMON_GOALS = ["Lose weight", "Increase energy", "Better sleep", "Build muscle", "Optimize hormones", "Reduce inflammation", "Improve focus", "Anti-aging", "Gut healing", "Athletic performance"];

// Stable component - defined outside to prevent unmount/remount on parent re-render
const CustomInput = React.memo(({ onAdd, placeholder }) => {
  const [val, setVal] = useState('');
  const handleAdd = () => {
    if (val.trim()) { onAdd(val.trim()); setVal(''); }
  };
  return (
    <div className="flex gap-2">
      <Input placeholder={placeholder} value={val} onChange={(e) => setVal(e.target.value)} className="text-sm"
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }} />
      <Button type="button" variant="outline" size="sm" onClick={handleAdd}>Add</Button>
    </div>
  );
});

const AssessmentWizard = ({ assessment, setAssessment, assessmentId, setAssessmentId, setGeneratedProtocol, setCurrentView }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [protocolProgress, setProtocolProgress] = useState(null);
  const [labAnalysisData, setLabAnalysisData] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [extractionPreview, setExtractionPreview] = useState(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [includeCustomFormulations, setIncludeCustomFormulations] = useState(true); // B2: Default ON

  const fullNameRef = useRef(null);
  const weightRef = useRef(null);
  const ageRef = useRef(null);
  const emailRef = useRef(null);
  // Ref mirror of `assessment` — ensures the Next button handler always reads the latest state
  // even when the user selects a value and immediately clicks Next before React re-renders.
  const assessmentRef = useRef(assessment);
  useEffect(() => { assessmentRef.current = assessment; }, [assessment]);

  const handleAssessmentChange = useCallback((field, value) => {
    assessmentRef.current = { ...assessmentRef.current, [field]: value };
    setAssessment(prev => ({ ...prev, [field]: value }));
  }, [setAssessment]);

  const handleLifestyleFactorChange = (factor, value) => {
    setAssessment(prev => ({ ...prev, lifestyle_factors: { ...prev.lifestyle_factors, [factor]: value } }));
  };

  const toggleListItem = (field, value) => {
    setAssessment(prev => {
      const list = prev[field] || [];
      return { ...prev, [field]: list.includes(value) ? list.filter(i => i !== value) : [...list, value] };
    });
  };

  const addItem = useCallback((field, value) => {
    if (value.trim()) setAssessment(prev => ({ ...prev, [field]: [...(prev[field] || []), value.trim()] }));
  }, [setAssessment]);

  const removeFromListField = (field, index) => {
    setAssessment(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const saveAssessmentStep = async (step, data) => {
    try {
      const response = await axios.post(`${API}/assessment/multi-step${assessmentId ? `?assessment_id=${assessmentId}` : ''}`, { step, assessment_data: data }, { headers: getAuthHeaders() });
      if (!assessmentId) setAssessmentId(response.data.id);
    } catch (error) { console.error('Save error:', error); }
  };

  const generateFunctionalProtocol = async () => {
    if (!assessmentId) { return; }
    setLoading(true);
    setProtocolProgress("Starting protocol generation...");
    try {
      // Step 1: kick off the background job
      const startRes = await axios.post(`${API}/generate-functional-protocol/${assessmentId}`, {
        include_custom_formulations: includeCustomFormulations,
      }, { headers: getAuthHeaders(), timeout: 30000 });
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        if (startRes.data?.protocol) { setGeneratedProtocol(startRes.data.protocol); setCurrentView('protocol'); setProtocolProgress(null); return; }
        throw new Error('No job ID returned');
      }
      // Step 2: poll until completed (up to 5 min, every 2s)
      const msgs = ["Analyzing patient data...", "Assembling clinical data...", "Applying clinical reasoning...", "Generating personalized protocol...", "Building comprehensive protocol...", "Cross-validating with research..."];
      let msgIdx = 0;
      for (let i = 0; i < 150; i++) {
        await new Promise(r => setTimeout(r, 2000));
        if (i % 3 === 0) { setProtocolProgress(msgs[msgIdx % msgs.length]); msgIdx++; }
        let pollData = null;
        try {
          const pollRes = await axios.get(`${API}/protocol-job/${jobId}`, { headers: getAuthHeaders(), timeout: 10000 });
          pollData = pollRes.data;
        } catch (pollErr) {
          // 404 = job not found, fatal
          if (pollErr.response?.status === 404) throw pollErr;
          // Any other network error (timeout, CORS, etc) = keep polling
          continue;
        }
        // Check status OUTSIDE try-catch so errors propagate to outer handler
        if (pollData?.status === 'completed' && pollData?.protocol) {
          setGeneratedProtocol(pollData.protocol); setCurrentView('protocol'); setProtocolProgress(null); return;
        }
        if (pollData?.status === 'failed') {
          throw new Error(pollData.error || 'Protocol generation failed');
        }
      }
      throw new Error('Protocol generation timed out after 5 minutes. Please try again.');
    } catch (error) {
      console.error('[Protocol Generation Error]', error);
      setProtocolProgress(null);
      alert(`Protocol generation error: ${error.message || 'Unknown error. Please try again.'}`);
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (files, fileType = 'labs') => {
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));
    formData.append('file_type', fileType);
    try {
      setUploadingFiles(true);
      const uploadUrl = `${API}/${fileType}/analyze`;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await axios.post(uploadUrl, formData, { timeout: 120000 });
          if (response.data?.analyses) {
            setExtractionPreview({ files: response.data.analyses, rawData: response.data, fileType });
            setShowExtractionModal(true);
            setUploadedFiles(prev => [...prev, ...response.data.analyses.map(a => ({...a, fileType}))]);
            setLabAnalysisData(response.data.analyses);
            setAssessment(prev => ({ ...prev, lab_analysis: response.data.analyses, uploaded_files: [...(prev.uploaded_files || []), ...response.data.analyses] }));
            return;
          }
        } catch (apiError) { if (attempt < 1) await new Promise(r => setTimeout(r, 2000)); }
      }
      const fallback = Array.from(files).map(file => ({ filename: file.name, filesize: file.size, fileType, analysis: { extracted_text: `${file.name}\n\nProcessing unavailable. Please try again.`, status: 'pending' } }));
      setExtractionPreview({ files: fallback, fallback: true, fileType });
      setShowExtractionModal(true);
      setUploadedFiles(prev => [...prev, ...fallback]);
    } catch (error) {} finally { setUploadingFiles(false); }
  };

  const removeUploadedFile = (filename) => setUploadedFiles(prev => prev.filter(f => f.filename !== filename));

  const applyExtractedData = () => {
    if (!extractionPreview) return;
    setLabAnalysisData(extractionPreview.files);
    setAssessment(prev => ({ ...prev, lab_analysis: extractionPreview.files, uploaded_files: [...(prev.uploaded_files || []), ...extractionPreview.files] }));
    setShowExtractionModal(false);
    setExtractionPreview(null);
  };

  const validateStep = (step, data) => {
    const errors = [];
    if (step === 1) {
      const name = fullNameRef.current?.value || data.patient_name || '';
      const age = ageRef.current?.value || data.age || '';
      const gender = data.gender || '';
      const weight = weightRef.current?.value || data.weight || '';
      if (!name.trim()) errors.push('Patient name is required');
      if (!age || isNaN(age) || Number(age) <= 0) errors.push('Valid age is required');
      if (!gender) errors.push('Gender is required');
      if (!weight || isNaN(weight) || Number(weight) <= 0) errors.push('Valid weight is required');
    }
    if (step === 2) {
      const concerns = data.primary_concerns || [];
      const goals = data.health_goals || [];
      if (concerns.length === 0 && goals.length === 0) {
        errors.push('Please select at least one health concern or goal before continuing');
      }
    }
    return errors;
  };

  const STEPS = ['Patient Info', 'Concerns & Goals', 'Medical History', 'Lifestyle', 'Labs & Genetics', 'Review'];

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6" data-testid="assessment-wizard">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Protocol Generator</h1>
        <p className="text-sm text-gray-500">Complete the assessment to generate a personalized peptide protocol</p>
      </div>

      {/* Progress */}
      <div className="space-y-3">
        <div className="flex justify-between text-xs text-gray-500">
          <span data-testid="step-counter">Step {currentStep} of 6</span>
          <span>{Math.round((currentStep / 6) * 100)}%</span>
        </div>
        <Progress value={(currentStep / 6) * 100} className="h-2" />
        <div className="hidden md:flex justify-between">
          {STEPS.map((step, i) => (
            <button key={i} onClick={() => { if (i + 1 < currentStep) setCurrentStep(i + 1); }}
              className={`text-[10px] md:text-xs px-1 py-0.5 rounded transition-colors ${currentStep === i + 1 ? 'text-teal-700 font-semibold' : currentStep > i + 1 ? 'text-green-600 cursor-pointer hover:text-green-700' : 'text-gray-400'}`}>
              {currentStep > i + 1 ? <CheckCircle className="h-3 w-3 inline mr-0.5" /> : null}{step}
            </button>
          ))}
        </div>
        <div className="flex md:hidden items-center justify-center gap-1.5">
          {STEPS.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full ${currentStep === i + 1 ? 'bg-teal-600 w-3 h-3' : currentStep > i + 1 ? 'bg-green-500' : 'bg-gray-300'}`} />
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6 md:p-8">

          {/* Step 1: Patient Info */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div><h2 className="text-xl font-semibold text-gray-900">Patient Information</h2><p className="text-sm text-gray-500 mt-1">Demographics for personalized dosing and protocol design</p></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Full Name *</Label>
                  <Input ref={fullNameRef} placeholder="Patient's full name" autoComplete="off" defaultValue={assessment.patient_name || ''} onBlur={(e) => handleAssessmentChange('patient_name', e.target.value)} data-testid="patient-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Age *</Label>
                  <Input ref={ageRef} type="number" placeholder="Age" defaultValue={assessment.age} onBlur={(e) => handleAssessmentChange('age', e.target.value)} data-testid="patient-age-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Gender *</Label>
                  <Select onValueChange={(v) => handleAssessmentChange('gender', v)} value={assessment.gender}>
                    <SelectTrigger data-testid="patient-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Weight *</Label>
                  <div className="flex gap-2">
                    <Input ref={weightRef} type="number" placeholder={assessment.weight_unit === 'kg' ? 'Weight in kg' : 'Weight in lbs'} defaultValue={assessment.weight || ''} onBlur={(e) => handleAssessmentChange('weight', e.target.value)} data-testid="patient-weight-input" className="flex-1" />
                    <Select onValueChange={(v) => {
                      const currentWeight = parseFloat(weightRef.current?.value || assessment.weight || 0);
                      handleAssessmentChange('weight_unit', v);
                      if (currentWeight > 0) {
                        const converted = v === 'kg' ? Math.round(currentWeight / 2.20462 * 10) / 10 : Math.round(currentWeight * 2.20462);
                        handleAssessmentChange('weight', converted.toString());
                        if (weightRef.current) weightRef.current.value = converted;
                      }
                    }} value={assessment.weight_unit || 'lbs'}>
                      <SelectTrigger className="w-20" data-testid="weight-unit-select"><SelectValue placeholder="lbs" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lbs">lbs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Height</Label>
                  <div className="flex gap-2">
                    <Select onValueChange={(v) => handleAssessmentChange('height_feet', v)} value={assessment.height_feet}>
                      <SelectTrigger><SelectValue placeholder="Ft" /></SelectTrigger>
                      <SelectContent>{[4,5,6,7].map(f => <SelectItem key={f} value={f.toString()}>{f} ft</SelectItem>)}</SelectContent>
                    </Select>
                    <Select onValueChange={(v) => handleAssessmentChange('height_inches', v)} value={assessment.height_inches}>
                      <SelectTrigger><SelectValue placeholder="In" /></SelectTrigger>
                      <SelectContent>{[0,1,2,3,4,5,6,7,8,9,10,11].map(i => <SelectItem key={i} value={i.toString()}>{i} in</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Email</Label>
                  <Input ref={emailRef} type="email" placeholder="patient@email.com" defaultValue={assessment.email} onBlur={(e) => handleAssessmentChange('email', e.target.value)} />
                </div>
              </div>
              {validationErrors.length > 0 && (
                <Alert variant="destructive" data-testid="validation-errors">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {validationErrors.map((err, i) => <li key={i} className="text-sm">{err}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Step 2: Concerns & Goals - with quick-select chips */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div><h2 className="text-xl font-semibold text-gray-900">Health Concerns & Goals</h2><p className="text-sm text-gray-500 mt-1">Select or type the patient's primary concerns and treatment goals</p></div>
              {validationErrors.length > 0 && (
                <Alert variant="destructive" data-testid="step2-validation-errors">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {validationErrors.map((err, i) => <li key={i} className="text-sm">{err}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <Label className="text-sm font-medium">Primary Concerns <span className="text-gray-400">(click to select)</span></Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_CONCERNS.map(c => (
                    <button key={c} onClick={() => toggleListItem('primary_concerns', c)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${(assessment.primary_concerns || []).includes(c) ? 'bg-teal-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'}`}>
                      {c}
                    </button>
                  ))}
                </div>
                <CustomInput onAdd={(v) => addItem('primary_concerns', v)} placeholder="Or type a custom concern and press Enter..." />
                {(assessment.primary_concerns || []).filter(c => !COMMON_CONCERNS.includes(c)).map((item, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer mr-1" onClick={() => removeFromListField('primary_concerns', (assessment.primary_concerns || []).indexOf(item))}>{item} x</Badge>
                ))}
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Health Goals <span className="text-gray-400">(click to select)</span></Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_GOALS.map(g => (
                    <button key={g} onClick={() => toggleListItem('health_goals', g)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${(assessment.health_goals || []).includes(g) ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`}>
                      {g}
                    </button>
                  ))}
                </div>
                <CustomInput onAdd={(v) => addItem('health_goals', v)} placeholder="Or type a custom goal and press Enter..." />
                {(assessment.health_goals || []).filter(g => !COMMON_GOALS.includes(g)).map((item, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer mr-1" onClick={() => removeFromListField('health_goals', (assessment.health_goals || []).indexOf(item))}>{item} x</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Medical History */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div><h2 className="text-xl font-semibold text-gray-900">Medical History</h2><p className="text-sm text-gray-500 mt-1">Current medications and conditions for safety screening</p></div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Current Medications & Supplements</Label>
                  <p className="text-xs text-gray-400">Include dose and frequency (e.g., "Metformin 500mg twice daily")</p>
                  <CustomInput onAdd={(v) => addItem('current_medications', v)} placeholder="Add medication..." />
                  <div className="flex flex-wrap gap-1">{(assessment.current_medications || []).map((m, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeFromListField('current_medications', i)}>{m} x</Badge>)}</div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Medical Conditions & History</Label>
                  <CustomInput onAdd={(v) => addItem('medical_history', v)} placeholder="Add condition (e.g., Type 2 diabetes, Hypertension)..." />
                  <div className="flex flex-wrap gap-1">{(assessment.medical_history || []).map((m, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeFromListField('medical_history', i)}>{m} x</Badge>)}</div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Known Allergies</Label>
                  <CustomInput onAdd={(v) => addItem('allergies', v)} placeholder="Add allergy..." />
                  <div className="flex flex-wrap gap-1">{(assessment.allergies || []).map((a, i) => <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeFromListField('allergies', i)}>{a} x</Badge>)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Lifestyle */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div><h2 className="text-xl font-semibold text-gray-900">Lifestyle Factors</h2><p className="text-sm text-gray-500 mt-1">Lifestyle data helps optimize peptide selection and dosing timing</p></div>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  { factor: 'exercise', label: 'Exercise', options: [["sedentary","Sedentary"],["light","Light (1-2x/week)"],["moderate","Moderate (3-4x/week)"],["active","Active (5-6x/week)"],["very_active","Very Active (daily)"]] },
                  { factor: 'sleep', label: 'Sleep Quality', options: [["poor","Poor"],["fair","Fair"],["good","Good"],["excellent","Excellent"]] },
                  { factor: 'stress', label: 'Stress Level', options: [["low","Low"],["moderate","Moderate"],["high","High"],["very_high","Very High"]] },
                  { factor: 'diet', label: 'Diet', options: [["standard","Standard"],["mediterranean","Mediterranean"],["keto","Ketogenic"],["paleo","Paleo"],["vegetarian","Vegetarian"],["vegan","Vegan"],["other","Other"]] },
                  { factor: 'alcohol', label: 'Alcohol Use', options: [["none","None"],["occasional","Occasional (1-2/week)"],["moderate","Moderate (3-5/week)"],["heavy","Heavy (daily)"]] },
                  { factor: 'smoking', label: 'Smoking Status', options: [["never","Never"],["former","Former"],["current","Current"],["vaping","Vaping/E-cig"]] },
                ].map(({ factor, label, options }) => (
                  <div key={factor} className="space-y-1.5">
                    <Label className="text-sm">{label}</Label>
                    <Select onValueChange={(v) => handleLifestyleFactorChange(factor, v)} value={assessment.lifestyle_factors?.[factor]}>
                      <SelectTrigger><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
                      <SelectContent>{options.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label className="text-sm">Current Supplements</Label>
                  <Input placeholder="e.g., Vitamin D, Fish Oil, Magnesium" value={assessment.lifestyle_factors?.supplements || ''} onChange={(e) => handleLifestyleFactorChange('supplements', e.target.value)} data-testid="lifestyle-supplements" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Occupation</Label>
                  <Input placeholder="e.g., Office worker, Construction, Healthcare" value={assessment.lifestyle_factors?.occupation || ''} onChange={(e) => handleLifestyleFactorChange('occupation', e.target.value)} data-testid="lifestyle-occupation" />
                </div>
              </div>
            </div>
          )}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div><h2 className="text-xl font-semibold text-gray-900">Labs & Genetics</h2><p className="text-sm text-gray-500 mt-1">Upload lab results or 23andMe data for deeper personalization. This step is optional.</p></div>
              <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'}`}
                data-testid="lab-drop-zone"
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={async (e) => { e.preventDefault(); setIsDragging(false); const files = Array.from(e.dataTransfer.files); if (files.length > 0) await handleFileUpload(files, 'labs'); }}>
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-3" />
                <p className="font-medium text-gray-700">Drag & drop files here</p>
                <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, or TXT</p>
                <div className="flex gap-3 justify-center mt-4">
                  <Button variant="outline" size="sm" disabled={uploadingFiles} data-testid="wizard-upload-labs-btn" onClick={() => document.getElementById('wizard-lab-input').click()}>
                    {uploadingFiles ? 'Analyzing...' : (<><Upload className="h-4 w-4 mr-1" /> Lab Results</>)}
                  </Button>
                  <Button variant="outline" size="sm" disabled={uploadingFiles} data-testid="wizard-upload-genetics-btn" onClick={() => document.getElementById('wizard-genetics-input').click()} className="border-purple-300 text-purple-700 hover:bg-purple-50">
                    {uploadingFiles ? 'Analyzing...' : 'Genetic Tests'}
                  </Button>
                </div>
                <input id="wizard-lab-input" type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.txt" onChange={async (e) => { const files = Array.from(e.target.files); if (files.length > 0) await handleFileUpload(files, 'labs'); e.target.value = ''; }} />
                <input id="wizard-genetics-input" type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.txt,.vcf,.23andme" onChange={async (e) => { const files = Array.from(e.target.files); if (files.length > 0) await handleFileUpload(files, 'genetics'); e.target.value = ''; }} />
              </div>
              {uploadedFiles.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                  <div className="text-sm font-medium text-green-800">Attached ({uploadedFiles.length})</div>
                  {uploadedFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-white rounded p-2 border border-green-200">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{f.fileType === 'genetics' ? 'GENETIC' : 'LAB'}</Badge>
                        <span>{f.filename}</span>
                      </div>
                      <button onClick={() => removeUploadedFile(f.filename)} className="text-red-500 hover:text-red-700 text-xs">remove</button>
                    </div>
                  ))}
                </div>
              )}
              {labAnalysisData && <Alert><CheckCircle className="h-4 w-4" /><AlertDescription>Analysis complete! Results will be used in protocol generation.</AlertDescription></Alert>}

              {/* Manual Lab Entry */}
              <div className="border border-gray-200 rounded-xl p-4">
                <button 
                  onClick={() => setAssessment(prev => ({ ...prev, showManualLabs: !prev.showManualLabs }))}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-700"
                  data-testid="manual-lab-toggle"
                >
                  <span>Enter Lab Values Manually</span>
                  <span className="text-xs text-gray-400">{assessment.showManualLabs ? 'Hide' : 'Show'}</span>
                </button>
                {assessment.showManualLabs && (
                  <div className="mt-4 space-y-4">
                    <p className="text-xs text-gray-500">Enter available lab values. Leave blank if unavailable.</p>
                    
                    {/* Thyroid Panel */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Thyroid Panel</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['TSH', 'Free T3', 'Free T4', 'TPO Antibodies'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Metabolic Panel */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Metabolic Panel</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['Fasting Glucose', 'HbA1c', 'Insulin', 'HOMA-IR'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Inflammatory Markers */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Inflammatory Markers</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['CRP', 'ESR', 'Homocysteine', 'Ferritin'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hormones */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Hormones</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['Total Testosterone', 'Free Testosterone', 'Estradiol', 'DHEA-S', 'Cortisol', 'IGF-1', 'Progesterone', 'SHBG'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Nutritional */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Nutritional</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['Vitamin D', 'Vitamin B12', 'Folate', 'Iron', 'Magnesium', 'Zinc'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CBC */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">CBC</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['WBC', 'RBC', 'Hemoglobin', 'Hematocrit', 'Platelets'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Liver & Kidney */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Liver & Kidney</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['ALT', 'AST', 'GGT', 'Creatinine', 'BUN', 'eGFR'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Lipids */}
                    <div>
                      <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-2">Lipid Panel</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {['Total Cholesterol', 'LDL', 'HDL', 'Triglycerides'].map(lab => (
                          <div key={lab}>
                            <Label className="text-[10px] text-gray-500">{lab}</Label>
                            <Input size="sm" placeholder={lab} data-testid={`lab-${lab.replace(/\s+/g,'-').toLowerCase()}`}
                              value={assessment.manual_labs?.[lab] || ''}
                              onChange={e => setAssessment(prev => ({ ...prev, manual_labs: { ...prev.manual_labs, [lab]: e.target.value } }))}
                              className="h-8 text-xs" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Review */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div><h2 className="text-xl font-semibold text-gray-900">Review & Generate</h2><p className="text-sm text-gray-500 mt-1">Review patient data before generating the protocol</p></div>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-gray-50"><CardContent className="p-4 text-sm space-y-1">
                  <div className="font-semibold text-gray-900 mb-2">Patient Info</div>
                  <div><span className="text-gray-500">Name:</span> {assessment.patient_name}</div>
                  <div><span className="text-gray-500">Age:</span> {assessment.age} | <span className="text-gray-500">Gender:</span> {assessment.gender}</div>
                  <div><span className="text-gray-500">Weight:</span> {assessment.weight} {assessment.weight_unit || 'lbs'}{(assessment.height_feet || assessment.height_inches) ? <> | <span className="text-gray-500">Height:</span> {assessment.height_feet || ''}'{assessment.height_inches || ''}"</> : ''}</div>
                </CardContent></Card>
                <Card className="bg-gray-50" data-testid="review-concerns-card"><CardContent className="p-4 text-sm space-y-1">
                  <div className="font-semibold text-gray-900 mb-2">Concerns & Goals</div>
                  <div className="flex flex-wrap gap-1" data-testid="review-concerns">{(assessment.primary_concerns || []).map((c, i) => <Badge key={i} className="text-[10px] bg-blue-100 text-teal-700">{c}</Badge>)}</div>
                  <div className="flex flex-wrap gap-1 mt-1" data-testid="review-goals">{(assessment.health_goals || []).map((g, i) => <Badge key={i} className="text-[10px] bg-green-100 text-green-700">{g}</Badge>)}</div>
                </CardContent></Card>
                {(assessment.current_medications?.length > 0 || assessment.medical_history?.length > 0 || assessment.allergies?.length > 0) && (
                  <Card className="bg-gray-50" data-testid="review-medical-card"><CardContent className="p-4 text-sm space-y-1">
                    <div className="font-semibold text-gray-900 mb-2">Medical</div>
                    {assessment.current_medications?.length > 0 && <div data-testid="review-medications"><span className="text-gray-500">Meds:</span> {[...new Set(assessment.current_medications)].join(', ')}</div>}
                    {assessment.medical_history?.length > 0 && <div data-testid="review-history"><span className="text-gray-500">History:</span> {assessment.medical_history.join(', ')}</div>}
                    {assessment.allergies?.length > 0 && <div data-testid="review-allergies"><span className="text-gray-500">Allergies:</span> {assessment.allergies.join(', ')}</div>}
                  </CardContent></Card>
                )}
                {/* Lifestyle Data from Step 3 */}
                {assessment.lifestyle_factors && Object.keys(assessment.lifestyle_factors).some(k => assessment.lifestyle_factors[k]) && (
                  <Card className="bg-gray-50"><CardContent className="p-4 text-sm space-y-1">
                    <div className="font-semibold text-gray-900 mb-2">Lifestyle</div>
                    {assessment.lifestyle_factors.exercise && <div><span className="text-gray-500">Exercise:</span> {assessment.lifestyle_factors.exercise.replace(/_/g, ' ')}</div>}
                    {assessment.lifestyle_factors.sleep && <div><span className="text-gray-500">Sleep:</span> {assessment.lifestyle_factors.sleep}</div>}
                    {assessment.lifestyle_factors.stress && <div><span className="text-gray-500">Stress:</span> {assessment.lifestyle_factors.stress.replace(/_/g, ' ')}</div>}
                    {assessment.lifestyle_factors.diet && <div><span className="text-gray-500">Diet:</span> {assessment.lifestyle_factors.diet}</div>}
                  </CardContent></Card>
                )}
                {uploadedFiles.length > 0 && (
                  <Card className="bg-gray-50"><CardContent className="p-4 text-sm">
                    <div className="font-semibold text-gray-900 mb-2">Uploaded Files</div>
                    {uploadedFiles.map((f, i) => (
                      <div key={i} className="mb-2 last:mb-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px]">{f.fileType === 'genetics' ? 'GENETIC' : 'LAB'}</Badge>
                          <span className="text-xs font-medium text-gray-700">{f.filename}</span>
                        </div>
                        {f.analysis?.extracted_text && (
                          <div className="text-xs text-gray-500 bg-white rounded p-2 border max-h-24 overflow-y-auto whitespace-pre-wrap">
                            {f.analysis.extracted_text.substring(0, 300)}{f.analysis.extracted_text.length > 300 ? '...' : ''}
                          </div>
                        )}
                        {f.analysis?.interpretation && !f.analysis?.extracted_text && (
                          <div className="text-xs text-gray-500 bg-white rounded p-2 border max-h-24 overflow-y-auto whitespace-pre-wrap">
                            {f.analysis.interpretation.substring(0, 300)}{f.analysis.interpretation.length > 300 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent></Card>
                )}
                {assessment.manual_labs && Object.values(assessment.manual_labs).some(v => v) && (
                  <Card className="bg-gray-50" data-testid="review-manual-labs"><CardContent className="p-4 text-sm">
                    <div className="font-semibold text-gray-900 mb-2">Manual Lab Values</div>
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(assessment.manual_labs).filter(([, v]) => v).map(([name, val]) => (
                        <div key={name} className="text-xs"><span className="text-gray-500">{name}:</span> {val}</div>
                      ))}
                    </div>
                  </CardContent></Card>
                )}
              </div>
              {/* B2: Custom Formulations Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-3" data-testid="custom-formulations-toggle">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Custom Formulations: <span className={`font-bold ${includeCustomFormulations ? 'text-green-600' : 'text-gray-400'}`}>{includeCustomFormulations ? 'ON' : 'OFF'}</span>
                  </p>
                  <p className="text-xs text-slate-500">{includeCustomFormulations ? 'M-51, RG-5555, N-5550 are eligible candidates' : 'Only standard individual peptides'}</p>
                </div>
                <Switch
                  checked={includeCustomFormulations}
                  onCheckedChange={(checked) => setIncludeCustomFormulations(checked)}
                  data-testid="custom-formulations-switch"
                  className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                />
              </div>
              <Button onClick={generateFunctionalProtocol} disabled={loading} data-testid="generate-protocol-btn"
                className="w-full bg-teal-600 hover:bg-teal-700 py-4 text-base">
                {loading ? (
                  <div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> {protocolProgress || "Generating..."}</div>
                ) : (
                  <div className="flex items-center gap-2"><Zap className="h-5 w-5" /> Generate Personalized Protocol</div>
                )}
              </Button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4 mt-4 border-t sticky bottom-0 bg-white z-40 pb-4 -mx-4 px-4 md:static md:pb-0 md:mx-0 md:px-0">
            <Button variant="outline" disabled={currentStep === 1 || loading} data-testid="assessment-prev-btn"
              onClick={() => { setCurrentStep(Math.max(1, currentStep - 1)); window.scrollTo({ top: 0, behavior: 'instant' }); }}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button disabled={currentStep === 6 || loading} data-testid="assessment-next-btn"
              onClick={() => {
                if (currentStep < 6) {
                  // Read from ref to avoid stale-closure race (e.g. user selects gender then clicks Next immediately)
                  const latest = assessmentRef.current;
                  let updated = { ...latest };
                  if (currentStep === 1) {
                    updated = { ...latest, patient_name: fullNameRef.current?.value || latest.patient_name || '', weight: weightRef.current?.value || latest.weight || '', age: ageRef.current?.value || latest.age || '', email: emailRef.current?.value || latest.email || '', gender: latest.gender || '' };
                    assessmentRef.current = updated;
                    setAssessment(updated);
                    const errors = validateStep(1, updated);
                    if (errors.length > 0) {
                      setValidationErrors(errors);
                      return;
                    }
                    setValidationErrors([]);
                  }
                  if (currentStep === 2) {
                    const errors = validateStep(2, latest);
                    if (errors.length > 0) {
                      setValidationErrors(errors);
                      return;
                    }
                    setValidationErrors([]);
                  }
                  setCurrentStep(currentStep + 1);
                  window.scrollTo({ top: 0, behavior: 'instant' });
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                  saveAssessmentStep(currentStep, updated).catch(() => {});
                }
              }}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Extraction Modal */}
      {showExtractionModal && extractionPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">{extractionPreview.fallback ? 'Files Attached' : (extractionPreview.fileType === 'genetics' ? 'Genetic Analysis Complete!' : 'Lab Analysis Complete!')}</h2>
              <p className="text-sm text-gray-500 mt-1">Review extracted data before adding to assessment.</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {extractionPreview.files.map((file, idx) => (
                <div key={idx} className={`mb-4 rounded-lg p-4 border ${file.fileType === 'genetics' ? 'bg-purple-50 border-purple-200' : 'bg-teal-50 border-teal-200'}`}>
                  <div className="font-semibold mb-2">{file.filename}</div>
                  {/* C7: Non-medical file warning */}
                  {file.analysis?.non_medical_warning && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex gap-2" data-testid="non-medical-warning">
                      <span className="text-amber-600 text-lg">⚠</span>
                      <p className="text-sm text-amber-800">{file.analysis.non_medical_warning}</p>
                    </div>
                  )}
                  {/* C5: Genetic SNP findings table with Confirm/Skip per row */}
                  {file.fileType === 'genetics' && file.analysis?.genetic_profile?.findings?.length > 0 ? (
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-purple-800 mb-2">Extracted Genetic Variants:</div>
                      <div className="overflow-x-auto bg-white rounded border">
                        <table className="w-full text-xs" data-testid="genetic-findings-table">
                          <thead className="bg-purple-100"><tr><th className="px-2 py-1 text-left">Gene</th><th className="px-2 py-1 text-left">Variant</th><th className="px-2 py-1 text-left">Genotype</th><th className="px-2 py-1 text-left">Result</th><th className="px-2 py-1 text-left">Category</th></tr></thead>
                          <tbody>
                            {file.analysis.genetic_profile.findings.slice(0, 20).map((f, i) => (
                              <tr key={i} className={f.result !== 'Wild Type' ? 'bg-amber-50' : ''}>
                                <td className="px-2 py-1 font-medium">{f.gene}</td>
                                <td className="px-2 py-1 text-slate-500">{f.rs_id}</td>
                                <td className="px-2 py-1">{f.genotype}</td>
                                <td className="px-2 py-1">{f.result}</td>
                                <td className="px-2 py-1 text-slate-500">{f.category}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[10px] text-purple-600 mt-1 italic">These findings will be incorporated into the protocol (Section 13: Genetic & Lab Integration)</p>
                    </div>
                  ) : (
                    <pre className="text-sm whitespace-pre-wrap font-mono bg-white rounded p-3 border max-h-48 overflow-y-auto">
                      {file.analysis?.extracted_text || file.analysis?.interpretation || JSON.stringify(file.analysis, null, 2)}
                    </pre>
                  )}
                  {file.analysis?.protocol_adjustments?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-sm font-semibold text-purple-800">Protocol Adjustments Based on Genetics:</div>
                      {file.analysis.protocol_adjustments.map((adj, ai) => (
                        <div key={ai} className="bg-white rounded p-3 border border-purple-100" data-testid={`genetic-adjustment-${ai}`}>
                          <div className="text-xs font-bold text-purple-700 mb-1">{adj.category}</div>
                          <ul className="text-xs text-slate-600 space-y-0.5">
                            {adj.adjustments.map((a, j) => <li key={j}>- {a}</li>)}
                          </ul>
                          {adj.priority_peptides?.length > 0 && (
                            <div className="text-xs text-emerald-600 mt-1">Priority: {adj.priority_peptides.join(', ')}</div>
                          )}
                          {adj.deprioritize_peptides?.length > 0 && (
                            <div className="text-xs text-amber-600 mt-1">Consider alternatives to: {adj.deprioritize_peptides.join(', ')}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {file.analysis?.key_findings?.length > 0 && !file.analysis?.protocol_adjustments?.length && (
                    <div className="mt-2 text-xs text-slate-500">
                      <span className="font-semibold">Key findings:</span> {file.analysis.key_findings.slice(0, 5).join('; ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t bg-gray-50 flex gap-3">
              <Button onClick={applyExtractedData} className="flex-1" data-testid="wizard-add-to-assessment-btn">
                {extractionPreview?.fileType === 'genetics' ? 'Confirm — Include in Protocol' : 'Add to Assessment'}
              </Button>
              <Button variant="outline" onClick={() => { setShowExtractionModal(false); setExtractionPreview(null); }}>Dismiss</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentWizard;
